/**
 * Tree Level - Activity 2: Pepe the Algarrobo
 * Meet Pepe's friends and practice breathing!
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tree } from './Tree';
import { useNarration } from '../../../hooks/useNarration';
import { animations } from '../../../lib/animationVariants';
import { VoiceEnableButton } from '../../ui/VoiceEnableButton';
import { soundManager } from '../../../lib/soundManager';
import { useLanguage } from '../../../contexts/LanguageContext';
import { track } from '@vercel/analytics';

type GameStage = 'intro' | 'discovery' | 'breathing' | 'complete';
type BreathingPhase = 'inhale' | 'exhale';

// ── Pepe-specific design tokens ─────────────────────────────────────────
// Pepe lives in the same design system as the other games but uses a
// slightly distinct voice: ALL Fraunces (no Inter for body), italic for
// his spoken lines, and a deeper "forest canopy" green so he reads as a
// wise, soft-spoken character. The brand green stays for primary CTAs so
// the system still cohesively connects to the rest of the app.
const PEPE_INK = '#1f4d3e';        // deep forest canopy — Pepe's primary text
const PEPE_GOLD = '#c9a961';       // Wonderpath gold (matches GlobalNav Beta sticker)
const BRAND_GREEN = '#42a57a';     // shared CTA green
const PEPE_INHALE = '#5b9dd6';     // calmer than the previous bright #3B82F6
const PEPE_EXHALE = BRAND_GREEN;   // brand-green softer than the previous #10B981
const SERIF = "'Fraunces', Georgia, serif";

interface TreeLevelProps {
  onBackToHome?: () => void;
}

export function TreeLevel({ onBackToHome }: TreeLevelProps) {
  const { language } = useLanguage();

  // Translations for all UI text
  const translations = {
    en: {
      home: 'Home',
      listening: 'Listening...',
      hello: "Hello! I'm Pepe",
      algarrobo: "I'm an algarrobo tree. I've lived here for many, many years...",
      hasFriends: 'and I have many friends who live with me. Would you like to meet them?',
      yes: 'Yes, Pepe!',
      tapTree: 'Tap the tree to find my friends',
      foundAll: 'I found them all!',
      greatJob: 'Great job! Look for',
      more: 'more...',
      breatheWithMe: 'Breathe with me',
      windBees: 'The wind moves through my branches... Breathe when I breathe',
      start: 'Start',
      breatheIn: 'Breathe in...',
      breatheOut: 'Breathe out...',
      wonderful: 'Wonderful!',
      thankYou: 'Thank you for breathing with me! I feel calm and happy.',
      comeBack: 'Come back anytime!',
      again: 'Visit Again',
      breathMore: 'Breathe More',
      finish: 'Finish',
      goHome: 'Home',
    },
    es: {
      home: 'Inicio',
      listening: 'Escuchando...',
      hello: '¡Hola! Soy Pepe',
      algarrobo: 'Soy un árbol de algarrobo. He vivido aquí por muchos, muchos años...',
      hasFriends: 'y tengo muchos amigos que viven conmigo. ¿Te gustaría conocerlos?',
      yes: '¡Sí, Pepe!',
      tapTree: 'Toca el árbol para encontrar a mis amigos',
      foundAll: '¡Los encontré a todos!',
      greatJob: '¡Buen trabajo! Busca',
      more: 'más...',
      breatheWithMe: 'Respira conmigo',
      windBees: 'El viento mueve mis ramas... Respira cuando yo respiro',
      start: 'Comenzar',
      breatheIn: 'Inhala...',
      breatheOut: 'Exhala...',
      wonderful: '¡Maravilloso!',
      thankYou: '¡Gracias por respirar conmigo! Me siento tranquilo y feliz.',
      comeBack: '¡Vuelve cuando quieras!',
      again: 'Visitar Otra Vez',
      breathMore: 'Respirar Más',
      finish: 'Terminar',
      goHome: 'Inicio',
    },
  };

  const t = translations[language];

  // Discovery items - translated based on language
  const getDiscoveries = () => {
    const items = language === 'es' ? {
      bird1: { x: 65, y: 25, label: 'Benteveo', sound: '🎵' },
      bird2: { x: 30, y: 35, label: 'Paloma', sound: '🎵' },
      nest: { x: 50, y: 30, label: 'Nido', icon: '🪺' },
      bee: { x: 58, y: 38, label: 'Abeja', icon: '🐝' },
      butterfly: { x: 45, y: 45, label: 'Mariposa', icon: '🦋' },
      beetle: { x: 55, y: 60, label: 'Escarabajo', icon: '🪲' },
      pod: { x: 35, y: 55, label: 'Algarroba', icon: '🫘' },
      fungi: { x: 43, y: 65, label: 'Hongo', icon: '🍄' },
      cows: { x: 68, y: 60, label: 'Vacas', icon: '🐄' },
    } : {
      bird1: { x: 65, y: 25, label: 'Benteveo Bird', sound: '🎵' },
      bird2: { x: 30, y: 35, label: 'Dove', sound: '🎵' },
      nest: { x: 50, y: 30, label: 'Nest', icon: '🪺' },
      bee: { x: 58, y: 38, label: 'Bee', icon: '🐝' },
      butterfly: { x: 45, y: 45, label: 'Butterfly', icon: '🦋' },
      beetle: { x: 55, y: 60, label: 'Beetle', icon: '🪲' },
      pod: { x: 35, y: 55, label: 'Algarroba Pod', icon: '🫘' },
      fungi: { x: 43, y: 65, label: 'Mushroom', icon: '🍄' },
      cows: { x: 68, y: 60, label: 'Cows', icon: '🐄' },
    };
    return items;
  };

  const discoveries = getDiscoveries();
  const totalDiscoveries = Object.keys(discoveries).length;

  const [stage, setStage] = useState<GameStage>('intro');
  const [discovered, setDiscovered] = useState(new Set<string>());
  const [breathingPhase, setBreathingPhase] = useState<BreathingPhase>('inhale');
  const [showInstructions, setShowInstructions] = useState(true);
  const [breathingCycles, setBreathingCycles] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);

  const discoveryProgress = (discovered.size / totalDiscoveries) * 100;

  // Narration hooks - using selected language
  const { isPlaying: introPlaying } = useNarration({
    scriptId: 'tree_intro',
    autoPlay: stage === 'intro',
    onComplete: () => setStage('discovery'),
    language,
  });

  const { speak: celebrateComplete } = useNarration({
    scriptId: 'tree_complete',
    autoPlay: false,
    language,
  });

  // Track game start
  useEffect(() => {
    if (stage === 'discovery') {
      track('Game Started', {
        game: 'tree-level',
        gameName: 'Meet Pepe the Tree',
        language: language
      });
    }
  }, [stage, language]);

  // Breathing cycle
  useEffect(() => {
    if (stage === 'breathing' && !showInstructions) {
      const cycleTime = { inhale: 4000, exhale: 4000 };
      const timer = setTimeout(() => {
        if (breathingPhase === 'inhale') {
          setBreathingPhase('exhale');
        } else {
          // Completed one full cycle (inhale -> exhale)
          setBreathingPhase('inhale');
          const newCycles = breathingCycles + 1;
          setBreathingCycles(newCycles);

          // After 2 cycles, show celebration
          if (newCycles >= 2) {
            setShowCelebration(true);
            soundManager.playCelebration();
            soundManager.playSparkleSequence(10, 100);
          }
        }
      }, cycleTime[breathingPhase]);
      return () => clearTimeout(timer);
    }
  }, [stage, breathingPhase, showInstructions, breathingCycles]);

  // Check if all friends discovered
  useEffect(() => {
    if (discovered.size >= totalDiscoveries && stage === 'discovery') {
      // Auto-advance to breathing stage after short delay
      setTimeout(() => {
        setStage('breathing');
        setShowInstructions(true);
      }, 1500);
    }
  }, [discovered.size, totalDiscoveries, stage]);

  const handleDiscovery = (key: string) => {
    if (!discovered.has(key)) {
      setDiscovered(new Set([...discovered, key]));
      soundManager.playSparkleSequence(3, 150);
    }
  };

  const handleNextStage = () => {
    if (stage === 'intro') {
      setStage('discovery');
    } else if (stage === 'discovery' && discovered.size >= totalDiscoveries) {
      setStage('breathing');
      setShowInstructions(true);
    } else if (stage === 'breathing') {
      setStage('complete');
      celebrateComplete();

      // Track game completion
      track('Game Completed', {
        game: 'tree-level',
        gameName: 'Meet Pepe the Tree',
        friendsDiscovered: discovered.size,
        breathingCycles: breathingCycles,
        language: language
      });

      soundManager.playCelebration();
      soundManager.playSparkleSequence(15, 100);
    }
  };

  const handleReplay = () => {
    setStage('intro');
    setDiscovered(new Set());
    setBreathingPhase('inhale');
    setShowInstructions(true);
  };

  return (
    <div className="relative w-full h-full overflow-y-auto overflow-x-hidden">
      {/* Background gradients — fixed to viewport so they always fill the
          browser even while content scrolls or the parent page is taller. */}

      {/* Default background for intro and discovery stages */}
      {(stage === 'intro' || stage === 'discovery') && (
        <>
          <div className="fixed inset-0 -z-10 bg-gradient-to-b from-sky-300 via-sky-200 to-amber-50 pointer-events-none" />
          <div className="fixed bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-green-800/30 to-transparent z-10 pointer-events-none" />
        </>
      )}

      {/* Breathing stage background - changes color based on breathing phase */}
      {stage === 'breathing' && (
        <div
          className={`fixed inset-0 -z-10 transition-colors duration-[4000ms] ease-in-out pointer-events-none ${
            breathingPhase === 'inhale'
              ? 'bg-gradient-to-b from-[#34a52b] via-[#4ade80] to-[#86efac]'
              : 'bg-gradient-to-b from-sky-400 via-sky-300 to-green-100'
          }`}
        />
      )}

      {/* Completion stage background */}
      {stage === 'complete' && (
        <div className="fixed inset-0 -z-10 bg-gradient-to-b from-green-300/70 to-amber-300/70 pointer-events-none" />
      )}

      {/* Mobile viewport scaling wrapper */}
      <style>{`
        @media (max-width: 768px) {
          .game-container {
            /* Minimal scaling for better mobile experience */
            transform: scale(1);
            transform-origin: center center;
          }
          .pepe-tree-mobile {
            width: 1400px !important;
            height: 1866px !important;
          }
          /* Larger discovery circles for easier tapping */
          .discovery-circle-mobile {
            r: 8 !important;
            stroke-width: 1.2 !important;
          }
          .discovery-icon-mobile {
            font-size: 7 !important;
          }
          .discovery-label-mobile {
            font-size: 2.5 !important;
          }
        }
        @media (max-width: 480px) {
          .game-container {
            /* No scaling on small phones */
            transform: scale(1);
            transform-origin: center center;
          }
          .pepe-tree-mobile {
            width: 1200px !important;
            height: 1600px !important;
          }
          /* Even larger discovery circles on small phones */
          .discovery-circle-mobile {
            r: 10 !important;
            stroke-width: 1.5 !important;
          }
          .discovery-icon-mobile {
            font-size: 8 !important;
          }
          /* Hide labels on very small screens to prevent overlap */
          .discovery-label-mobile {
            display: none !important;
          }
        }
      `}</style>

      {/* Voice enable button - positioned well below the nav, inside the body */}
      <div
        style={{
          position: 'absolute',
          top: 'clamp(160px, 18vw, 220px)',
          right: '20px',
          zIndex: 40,
        }}
      >
        <VoiceEnableButton />
      </div>

      <div className="game-container relative w-full min-h-screen">

        {/* INTRO STAGE */}
        {stage === 'intro' && (
          <div className="relative min-h-screen flex items-center justify-center py-20">
            <div className="text-center px-8 animate-fadeIn max-w-4xl">
              <motion.div
                className="mb-2"
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              >
                <img
                  src="/Pepe-intro-screen.svg?v=2"
                  alt="Pepe the Algarrobo Tree"
                  className="w-[400px] h-[400px] object-contain mx-auto"
                />
              </motion.div>
              <h1
                className="animate-slideUp"
                style={{
                  fontFamily: SERIF,
                  fontWeight: 300,
                  fontSize: 'clamp(40px, 7vw, 84px)',
                  color: PEPE_INK,
                  letterSpacing: '-0.015em',
                  lineHeight: 1.05,
                  marginBottom: 16,
                }}
              >
                {t.hello}
              </h1>
              <p
                className="animate-slideUp delay-200 max-w-2xl mx-auto"
                style={{
                  fontFamily: SERIF,
                  fontStyle: 'italic',
                  fontSize: 'clamp(22px, 3.4vw, 36px)',
                  fontWeight: 500,
                  color: PEPE_INK,
                  marginBottom: 18,
                }}
              >
                {t.algarrobo}
              </p>
              <p
                className="animate-slideUp delay-400 max-w-xl mx-auto"
                style={{
                  fontFamily: SERIF,
                  fontSize: 'clamp(18px, 2.4vw, 26px)',
                  fontWeight: 400,
                  color: PEPE_INK,
                  opacity: 0.78,
                  marginBottom: 32,
                }}
              >
                {t.hasFriends}
              </p>
              <button
                onClick={handleNextStage}
                className="animate-slideUp delay-600"
                style={{
                  background: BRAND_GREEN,
                  color: '#ffffff',
                  fontFamily: SERIF,
                  fontWeight: 600,
                  fontSize: 'clamp(18px, 2.4vw, 24px)',
                  padding: '16px 40px',
                  borderRadius: 999,
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 6px 22px rgba(66,165,122,0.32)',
                }}
              >
                {t.yes}
              </button>
            </div>
          </div>
        )}

        {/* DISCOVERY STAGE */}
        {stage === 'discovery' && (
          <div className="relative min-h-screen flex flex-col">
            {/* Progress header */}
            <div className="flex-shrink-0 pt-4 pb-2 px-4 text-center z-20 animate-fadeIn">
              <div
                className="inline-block px-7 py-4"
                style={{
                  background: 'rgba(255,255,255,0.92)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: 28,
                  border: `1px solid ${PEPE_GOLD}33`,
                  boxShadow: '0 6px 18px rgba(31,77,62,0.08)',
                }}
              >
                <p
                  style={{
                    fontFamily: SERIF,
                    fontStyle: 'italic',
                    fontWeight: 500,
                    fontSize: 'clamp(20px, 2.6vw, 28px)',
                    color: PEPE_INK,
                  }}
                >
                  {t.tapTree}
                </p>
                <div className="mt-3 flex items-center justify-center gap-3">
                  <div
                    style={{
                      background: 'rgba(31,77,62,0.1)',
                      width: 160,
                      height: 6,
                      borderRadius: 999,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      className="transition-all duration-500"
                      style={{
                        background: PEPE_GOLD,
                        width: `${Math.max(discoveryProgress, 10)}%`,
                        height: '100%',
                        borderRadius: 999,
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontFamily: SERIF,
                      fontWeight: 600,
                      fontSize: 16,
                      color: PEPE_INK,
                      minWidth: 60,
                      textAlign: 'left',
                    }}
                  >
                    {discovered.size} / {totalDiscoveries}
                  </span>
                </div>
              </div>
            </div>

            {/* Tree with discoveries - centered container */}
            <div className="flex-1 flex items-center justify-center py-4">
              <Tree
                discovered={discovered}
                discoveries={discoveries}
                onDiscovery={handleDiscovery}
                breathingPhase="idle"
                showDiscoveryHints={true}
              />
            </div>

            {/* Progress messages */}
            {discovered.size > 0 && discovered.size < totalDiscoveries && (
              <div className="flex-shrink-0 pb-32 px-4 text-center animate-fadeIn">
                <div
                  className="inline-block px-6 py-3"
                  style={{
                    background: 'rgba(255,255,255,0.92)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: 22,
                    border: `1px solid ${PEPE_GOLD}33`,
                    boxShadow: '0 6px 18px rgba(31,77,62,0.08)',
                  }}
                >
                  <p
                    style={{
                      fontFamily: SERIF,
                      fontStyle: 'italic',
                      fontSize: 'clamp(18px, 2.2vw, 22px)',
                      color: PEPE_INK,
                    }}
                  >
                    {t.greatJob} {totalDiscoveries - discovered.size} {t.more}
                  </p>
                </div>
              </div>
            )}

            {/* All found button */}
            {discovered.size >= totalDiscoveries && (
              <div className="flex-shrink-0 pb-32 px-4 text-center animate-fadeIn">
                <motion.button
                  onClick={handleNextStage}
                  style={{
                    background: BRAND_GREEN,
                    color: '#ffffff',
                    fontFamily: SERIF,
                    fontWeight: 600,
                    fontSize: 'clamp(18px, 2.4vw, 24px)',
                    padding: '16px 40px',
                    borderRadius: 999,
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 6px 22px rgba(66,165,122,0.32)',
                  }}
                  animate={animations.pulse}
                >
                  {t.foundAll} →
                </motion.button>
              </div>
            )}
          </div>
        )}

        {/* BREATHING STAGE */}
        {stage === 'breathing' && (
          <div className="relative w-full min-h-screen flex items-center justify-center">
            {/* Instructions overlay */}
            {showInstructions && (
              <>
                {/* Initial breathing tree */}
                <Tree
                  discovered={discovered}
                  discoveries={discoveries}
                  breathingPhase="idle"
                  showDiscoveryHints={false}
                  isInitialBreathing={true}
                />

                <div className="absolute top-20 left-0 right-0 text-center z-20 animate-fadeIn px-4">
                  <div
                    className="inline-block px-8 py-7 max-w-2xl mx-auto"
                    style={{
                      background: 'rgba(255,255,255,0.94)',
                      backdropFilter: 'blur(8px)',
                      borderRadius: 28,
                      border: `1px solid ${PEPE_GOLD}33`,
                      boxShadow: '0 10px 30px rgba(31,77,62,0.12)',
                    }}
                  >
                    <p
                      style={{
                        fontFamily: SERIF,
                        fontWeight: 300,
                        fontSize: 'clamp(28px, 4vw, 44px)',
                        color: PEPE_INK,
                        letterSpacing: '-0.015em',
                        marginBottom: 12,
                      }}
                    >
                      {t.breatheWithMe}
                    </p>
                    <p
                      style={{
                        fontFamily: SERIF,
                        fontStyle: 'italic',
                        fontWeight: 500,
                        fontSize: 'clamp(18px, 2.4vw, 24px)',
                        color: PEPE_INK,
                        marginBottom: 22,
                      }}
                    >
                      {t.windBees}
                    </p>
                    <button
                      onClick={() => setShowInstructions(false)}
                      style={{
                        background: BRAND_GREEN,
                        color: '#ffffff',
                        fontFamily: SERIF,
                        fontWeight: 600,
                        fontSize: 'clamp(16px, 2vw, 20px)',
                        padding: '14px 32px',
                        borderRadius: 999,
                        border: 'none',
                        cursor: 'pointer',
                        boxShadow: '0 6px 22px rgba(66,165,122,0.32)',
                      }}
                    >
                      {t.start}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Breathing exercise */}
            {!showInstructions && (
              <>
                {/* Breathing instruction — italic Fraunces, calm tones.
                    Inhale = soft sky, exhale = brand-green canopy. */}
                <div className="absolute top-24 left-0 right-0 text-center z-20 px-4">
                  <div
                    className="inline-block px-8 py-4"
                    style={{
                      background: 'rgba(255,255,255,0.86)',
                      backdropFilter: 'blur(8px)',
                      borderRadius: 28,
                      border: `1px solid ${PEPE_GOLD}33`,
                    }}
                  >
                    <p
                      style={{
                        fontFamily: SERIF,
                        fontStyle: 'italic',
                        fontWeight: 500,
                        fontSize: 'clamp(36px, 5vw, 56px)',
                        letterSpacing: '-0.01em',
                        color: breathingPhase === 'inhale' ? PEPE_INHALE : PEPE_EXHALE,
                        transition: 'color 1200ms ease',
                      }}
                    >
                      {breathingPhase === 'inhale'
                        ? `↑ ${t.breatheIn}`
                        : `↓ ${t.breatheOut}`}
                    </p>
                  </div>
                </div>

                {/* Animated tree */}
                <Tree
                  discovered={discovered}
                  discoveries={discoveries}
                  breathingPhase={breathingPhase}
                  showDiscoveryHints={false}
                  isInitialBreathing={false}
                />

                {/* Floating leaves */}
                <div className="absolute inset-0 pointer-events-none">
                  {[...Array(breathingPhase === 'exhale' ? 8 : 5)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute text-6xl animate-float"
                      style={{
                        left: `${15 + i * 12}%`,
                        top: `${10 + (i % 3) * 15}%`,
                        animationDelay: `${i * 0.3}s`,
                        animationDuration:
                          breathingPhase === 'exhale' ? `${2 + i * 0.5}s` : `${4 + i}s`,
                        opacity: breathingPhase === 'exhale' ? 1 : 0.6,
                      }}
                    >
                      🍃
                    </div>
                  ))}
                </div>

                {/* Celebration confetti and hearts after 2 cycles */}
                {showCelebration && (
                  <div className="absolute inset-0 pointer-events-none z-30">
                    {/* Confetti */}
                    {Array.from({ length: 40 }).map((_, i) => (
                      <motion.div
                        key={`confetti-${i}`}
                        className="absolute text-5xl"
                        initial={{
                          x: typeof window !== 'undefined' ? Math.random() * window.innerWidth : 0,
                          y: -50,
                          rotate: 0,
                        }}
                        animate={{
                          y: typeof window !== 'undefined' ? window.innerHeight + 50 : 1000,
                          rotate: 360 * (Math.random() > 0.5 ? 1 : -1),
                          x: typeof window !== 'undefined' ? Math.random() * window.innerWidth : 0,
                        }}
                        transition={{
                          duration: 3 + Math.random() * 2,
                          delay: Math.random() * 0.5,
                          ease: 'linear',
                        }}
                      >
                        {['🎊', '🎉', '⭐', '✨', '🌟', '💫'][Math.floor(Math.random() * 6)]}
                      </motion.div>
                    ))}

                    {/* Hearts */}
                    {Array.from({ length: 30 }).map((_, i) => (
                      <motion.div
                        key={`heart-${i}`}
                        className="absolute text-6xl"
                        initial={{
                          x: typeof window !== 'undefined' ? Math.random() * window.innerWidth : 0,
                          y: -50,
                          rotate: 0,
                          scale: 0.5 + Math.random() * 0.5,
                        }}
                        animate={{
                          y: typeof window !== 'undefined' ? window.innerHeight + 50 : 1000,
                          rotate: [0, 10, -10, 10, 0],
                          x: typeof window !== 'undefined'
                            ? Math.random() * window.innerWidth + Math.sin(i) * 100
                            : 0,
                        }}
                        transition={{
                          duration: 4 + Math.random() * 2,
                          delay: Math.random() * 0.8,
                          ease: 'easeOut',
                        }}
                      >
                        {['💚', '💛', '🧡', '💙', '💜'][Math.floor(Math.random() * 5)]}
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Breathing options (appears after 2 cycles) */}
                {breathingCycles >= 2 && (
                  <motion.div
                    className="absolute bottom-8 left-0 right-0 flex gap-4 justify-center z-40 px-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ type: 'spring', bounce: 0.5, duration: 0.8 }}
                  >
                    {/* Breathe More — outline-green secondary */}
                    <motion.button
                      onClick={() => {
                        setBreathingCycles(0);
                        setShowCelebration(false);
                        setBreathingPhase('inhale');
                      }}
                      style={{
                        background: 'rgba(255,255,255,0.92)',
                        color: PEPE_INK,
                        fontFamily: SERIF,
                        fontWeight: 600,
                        fontSize: 'clamp(16px, 2vw, 20px)',
                        padding: '14px 28px',
                        borderRadius: 999,
                        border: `1.5px solid ${BRAND_GREEN}`,
                        cursor: 'pointer',
                      }}
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {t.breathMore}
                    </motion.button>

                    {/* Finish — brand-green primary */}
                    <motion.button
                      onClick={handleNextStage}
                      style={{
                        background: BRAND_GREEN,
                        color: '#ffffff',
                        fontFamily: SERIF,
                        fontWeight: 600,
                        fontSize: 'clamp(16px, 2vw, 20px)',
                        padding: '14px 32px',
                        borderRadius: 999,
                        border: 'none',
                        cursor: 'pointer',
                        boxShadow: '0 6px 22px rgba(66,165,122,0.32)',
                      }}
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {t.finish} →
                    </motion.button>
                  </motion.div>
                )}
              </>
            )}
          </div>
        )}

        {/* COMPLETION STAGE */}
        <AnimatePresence>
          {stage === 'complete' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative w-full min-h-screen flex items-center justify-center z-50"
            >
              <motion.div
                className="text-center mx-4 p-8 sm:p-10 md:p-14 w-full max-w-2xl"
                style={{
                  background: '#ffffff',
                  border: `1px solid ${PEPE_GOLD}55`,
                  borderRadius: 28,
                  boxShadow: '0 12px 36px rgba(31,77,62,0.14)',
                }}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 220, damping: 22 }}
              >
                {/* Celebration tree — gentle sway, not spinning */}
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <img
                    src="/Pepe-intro-screen.svg?v=2"
                    alt="Pepe the Algarrobo Tree"
                    className="object-contain mx-auto"
                    style={{
                      width: 'clamp(180px, 35vw, 260px)',
                      height: 'clamp(180px, 35vw, 260px)',
                    }}
                  />
                </motion.div>

                <motion.h2
                  className="mt-6"
                  style={{
                    fontFamily: SERIF,
                    fontWeight: 300,
                    fontSize: 'clamp(36px, 6.5vw, 64px)',
                    color: PEPE_INK,
                    letterSpacing: '-0.015em',
                  }}
                  initial={{ y: 12, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.15 }}
                >
                  {t.wonderful}
                </motion.h2>

                <motion.p
                  className="mt-3"
                  style={{
                    fontFamily: SERIF,
                    fontStyle: 'italic',
                    fontWeight: 500,
                    fontSize: 'clamp(20px, 2.8vw, 28px)',
                    color: PEPE_INK,
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  {t.thankYou}
                </motion.p>

                <motion.p
                  className="mt-2"
                  style={{
                    fontFamily: SERIF,
                    fontWeight: 400,
                    fontSize: 'clamp(16px, 2vw, 20px)',
                    color: PEPE_INK,
                    opacity: 0.7,
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.7 }}
                  transition={{ delay: 0.45 }}
                >
                  {t.comeBack}
                </motion.p>

                <motion.div
                  className="flex gap-3 justify-center mt-7 flex-wrap"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <motion.button
                    onClick={handleReplay}
                    style={{
                      background: BRAND_GREEN,
                      color: '#ffffff',
                      fontFamily: SERIF,
                      fontWeight: 600,
                      fontSize: 'clamp(16px, 2vw, 20px)',
                      padding: '14px 32px',
                      borderRadius: 999,
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: '0 6px 22px rgba(66,165,122,0.32)',
                    }}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    {t.again}
                  </motion.button>

                  {onBackToHome && (
                    <motion.button
                      onClick={onBackToHome}
                      style={{
                        background: 'transparent',
                        color: PEPE_INK,
                        fontFamily: SERIF,
                        fontWeight: 600,
                        fontSize: 'clamp(16px, 2vw, 20px)',
                        padding: '14px 28px',
                        borderRadius: 999,
                        border: `1.5px solid ${PEPE_INK}`,
                        cursor: 'pointer',
                      }}
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {t.goHome}
                    </motion.button>
                  )}
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Confetti on completion — calm, leaf-only, no rainbow burst */}
        {stage === 'complete' && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 30 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute"
                style={{ fontSize: 'clamp(20px, 2.5vw, 32px)', lineHeight: 1, opacity: 0.85 }}
                initial={{
                  x: typeof window !== 'undefined' ? Math.random() * window.innerWidth : 0,
                  y: -50,
                  rotate: 0,
                }}
                animate={{
                  y: typeof window !== 'undefined' ? window.innerHeight + 50 : 1000,
                  rotate: 360 * (Math.random() > 0.5 ? 1 : -1),
                  x:
                    typeof window !== 'undefined'
                      ? Math.random() * window.innerWidth
                      : 0,
                }}
                transition={{
                  duration: 4 + Math.random() * 2,
                  delay: Math.random() * 0.8,
                  ease: 'linear',
                }}
              >
                {['🍃', '🌿', '✨'][Math.floor(Math.random() * 3)]}
              </motion.div>
            ))}
          </div>
        )}

        {/* Narration indicator — italic Fraunces in deep forest ink */}
        <AnimatePresence>
          {introPlaying && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-24 left-8 px-5 py-2.5"
              style={{
                background: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(8px)',
                borderRadius: 999,
                border: `1px solid ${PEPE_GOLD}33`,
                boxShadow: '0 6px 18px rgba(31,77,62,0.08)',
              }}
            >
              <span
                style={{
                  fontFamily: SERIF,
                  fontStyle: 'italic',
                  fontWeight: 500,
                  fontSize: 16,
                  color: PEPE_INK,
                }}
              >
                {t.listening}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes float {
          0%,
          100% {
            transform: translateY(0) rotate(0deg);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-50px) rotate(180deg);
            opacity: 1;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.8s ease-out;
        }
        .animate-slideUp.delay-200 {
          animation-delay: 0.2s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animate-slideUp.delay-400 {
          animation-delay: 0.4s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animate-slideUp.delay-600 {
          animation-delay: 0.6s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animate-float {
          animation: float linear infinite;
        }
      `}</style>
    </div>
  );
}
